/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Header } from './header';
import { Footer } from './Footer';
import { SEO } from '../base/SEO';
// Cambiar la línea de importación:
import { globalConfig } from '../../content'; // ✅ CORREGIDO

// Error Fallback Component
/**
 * ErrorFallback - Componente de error crítico del sistema
 * Maneja errores no capturados en la aplicación
 */
function ErrorFallback({ error, resetErrorBoundary, componentName }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary text-white p-4">
      <div className="max-w-md w-full text-center">
        <svg className="w-16 h-16 mx-auto text-error mb-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <h2 className="text-2xl font-bold mb-4 text-error">¡Oops! Algo salió mal</h2>
        <p className="text-gray-300 mb-6">
          Error en: <span className="font-mono text-warning">{componentName}</span>
        </p>
        <div className="space-y-3">
          <button
            onClick={resetErrorBoundary}
            className="w-full bg-secondary hover:bg-secondary/80 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Reintentar
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Ir al inicio
          </button>
        </div>
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-warning hover:text-warning/80">
            Ver detalles del error
          </summary>
          <pre className="mt-2 p-3 bg-gray-800 rounded text-xs text-error/80 overflow-auto">
            {error.stack}
          </pre>
        </details>
      </div>
    </div>
  );
}

// Loading Component
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-secondary mx-auto mb-4"></div>
        <p className="text-white font-sweet text-lg">Cargando...</p>
      </div>
    </div>
  );
}

// Safe Component Wrapper
const SafeComponent = ({ component: Component, fallbackName, ...props }) => {
  if (!Component) {
    console.warn(`Componente ${fallbackName} no encontrado`);
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded m-4">
        <strong>⚠️ Componente no disponible:</strong> {fallbackName}
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={(errorProps) => (
        <ErrorFallback {...errorProps} componentName={fallbackName} />
      )}
      onError={(error) => {
        console.error(`Error en ${fallbackName}:`, error);
        // Aquí podrías enviar el error a un servicio de monitoreo
      }}
    >
      <Suspense fallback={<LoadingFallback message={`Cargando ${fallbackName}...`} />}>
        <Component {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

// Main AppLayout Component
export const AppLayout = ({ 
  children, 
  pageConfig = {},
  headerOverrides = {},
  footerOverrides = {},
  showHeader = true,
  showFooter = true,
  loadingMessage = "Cargando página..."
}) => {
  // Safe config access
  const safeGlobalConfig = React.useMemo(() => {
    try {
      return globalConfig || {};
    } catch (error) {
      console.error('Error accediendo a globalConfig:', error);
      return {};
    }
  }, []);

  // Safe header data
  const headerData = React.useMemo(() => {
    try {
      return {
        ...safeGlobalConfig.header,
        ...headerOverrides
      };
    } catch (error) {
      console.error('Error preparando headerData:', error);
      return {};
    }
  }, [safeGlobalConfig.header, headerOverrides]);

  // Safe footer data
  const footerData = React.useMemo(() => {
    try {
      return {
        ...safeGlobalConfig.footer,
        ...footerOverrides
      };
    } catch (error) {
      console.error('Error preparando footerData:', error);
      return {};
    }
  }, [safeGlobalConfig.footer, footerOverrides]);

  return (
    <ErrorBoundary
      FallbackComponent={(errorProps) => (
        <ErrorFallback {...errorProps} componentName="AppLayout" />
      )}
      onError={(error) => {
        console.error('Error crítico en AppLayout:', error);
      }}
    >
      {/* SEO Global con fallback */}
      <SEO 
        title={pageConfig.meta?.title || 'Zero Latency VR'}
        description={pageConfig.meta?.description || 'Experiencia de Realidad Virtual'}
        keywords={pageConfig.meta?.keywords || 'VR, realidad virtual, gaming'}
      />
      
      {/* Header FIJO - siempre visible */}
      {showHeader && (
        <SafeComponent 
          component={Header}
          fallbackName="Header"
          data={headerData}
        />
      )}
      
      {/* Contenido Principal SIN Suspense - se maneja en App.jsx */}
      <ErrorBoundary
        FallbackComponent={(errorProps) => (
          <ErrorFallback {...errorProps} componentName="Contenido Principal" />
        )}
        onError={(error) => {
          console.error('Error en contenido principal:', error);
        }}
      >
        <main className="min-h-screen">
          {children}
        </main>
      </ErrorBoundary>
      
      {/* Footer FIJO - siempre visible */}
      {showFooter && (
        <SafeComponent 
          component={Footer}
          fallbackName="Footer"
          data={footerData}
        />
      )}
    </ErrorBoundary>
  );
};

export default AppLayout;

// Development Banner
function DevelopmentBanner() {
  return (
    <div className="bg-warning/10 border border-warning text-warning px-4 py-3 rounded m-4">
      <strong>Modo Desarrollo:</strong> Algunas funciones pueden no estar disponibles.
    </div>
  );
}